/**
 * PDF Branding Template Configuration System
 * 
 * This file provides a flexible template system for customizing
 * the appearance and branding of generated PDF oracle reports.
 */

// Default Oráculo dos 12 Raios branding
export const DEFAULT_BRANDING = {
  // Colors (hex codes or RGB values)
  colors: {
    primary: '#4A0E4E',      // Main purple color for titles
    secondary: '#8B5A8C',    // Secondary purple for accents
    accent: '#06B6D4',       // Cyan blue for highlights
    text: '#2D3748',         // Dark gray for main text
    textSecondary: '#718096', // Light gray for secondary text
    background: '#FFFFFF',    // White background
    sectionBorder: '#E2E8F0'  // Light gray for borders
  },

  // Typography
  fonts: {
    primary: 'helvetica',     // Main font family
    secondary: 'times',       // Secondary font for special content
    sizes: {
      title: 24,
      subtitle: 16,
      sectionTitle: 14,
      body: 11,
      caption: 9
    }
  },

  // Layout settings
  layout: {
    margins: {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20
    },
    spacing: {
      sectionGap: 20,
      paragraphGap: 10,
      lineHeight: 6
    }
  },

  // Branding elements
  branding: {
    title: 'Consulta ao Oráculo Violeta',
    subtitle: 'Relatório Completo da Sessão',
    footerText: 'Oráculo dos 12 Raios • v1.0 • by Buenaar',
    logoUrl: null, // Base64 encoded image or URL
    watermark: null // Subtle background watermark
  },

  // Section-specific styling
  sections: {
    blockage: {
      borderColor: '#EF4444',
      backgroundColor: null,
      titleColor: null // Uses primary if null
    },
    solution: {
      borderColor: '#10B981',
      backgroundColor: null,
      titleColor: null
    },
    wisdom: {
      borderColor: '#8B5A8C',
      backgroundColor: null,
      titleColor: null
    },
    decree: {
      borderColor: '#06B6D4',
      backgroundColor: '#F0F9FF',
      titleColor: '#0369A1',
      fontStyle: 'italic',
      fontWeight: 'bold'
    }
  }
};

// Example custom branding templates
export const BRANDING_TEMPLATES = {
  // Elegant minimalist template
  elegant: {
    ...DEFAULT_BRANDING,
    colors: {
      ...DEFAULT_BRANDING.colors,
      primary: '#1F2937',
      secondary: '#6B7280',
      accent: '#3B82F6'
    },
    branding: {
      ...DEFAULT_BRANDING.branding,
      title: 'Oracle Consultation',
      subtitle: 'Spiritual Guidance Report'
    }
  },

  // Mystical dark template
  mystical: {
    ...DEFAULT_BRANDING,
    colors: {
      ...DEFAULT_BRANDING.colors,
      primary: '#7C3AED',
      secondary: '#A855F7',
      accent: '#EC4899',
      text: '#1F2937',
      textSecondary: '#6B7280'
    },
    branding: {
      ...DEFAULT_BRANDING.branding,
      title: 'Consulta Mística',
      subtitle: 'Revelações do Oráculo'
    }
  },

  // Professional corporate template
  corporate: {
    ...DEFAULT_BRANDING,
    colors: {
      ...DEFAULT_BRANDING.colors,
      primary: '#1E40AF',
      secondary: '#3B82F6',
      accent: '#10B981',
      text: '#111827',
      textSecondary: '#6B7280'
    },
    fonts: {
      ...DEFAULT_BRANDING.fonts,
      primary: 'helvetica'
    },
    branding: {
      ...DEFAULT_BRANDING.branding,
      title: 'Oracle Report',
      subtitle: 'Professional Consultation Analysis'
    }
  }
};

/**
 * Merge custom branding configuration with default template
 * @param {Object} customBranding - Custom branding overrides
 * @param {string} template - Base template name ('default', 'elegant', 'mystical', 'corporate')
 * @returns {Object} - Merged branding configuration
 */
export const mergeBrandingConfig = (customBranding = {}, template = 'default') => {
  const baseTemplate = template === 'default' 
    ? DEFAULT_BRANDING 
    : BRANDING_TEMPLATES[template] || DEFAULT_BRANDING;

  return {
    colors: { ...baseTemplate.colors, ...customBranding.colors },
    fonts: { ...baseTemplate.fonts, ...customBranding.fonts },
    layout: { ...baseTemplate.layout, ...customBranding.layout },
    branding: { ...baseTemplate.branding, ...customBranding.branding },
    sections: { ...baseTemplate.sections, ...customBranding.sections }
  };
};

/**
 * Validate branding configuration
 * @param {Object} brandingConfig - Branding configuration to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateBrandingConfig = (brandingConfig) => {
  const errors = [];

  // Validate required colors
  const requiredColors = ['primary', 'secondary', 'accent', 'text'];
  requiredColors.forEach(color => {
    if (!brandingConfig.colors?.[color]) {
      errors.push(`Missing required color: ${color}`);
    }
  });

  // Validate font sizes
  if (brandingConfig.fonts?.sizes) {
    Object.entries(brandingConfig.fonts.sizes).forEach(([key, size]) => {
      if (typeof size !== 'number' || size < 6 || size > 72) {
        errors.push(`Invalid font size for ${key}: must be between 6-72`);
      }
    });
  }

  // Validate margins
  if (brandingConfig.layout?.margins) {
    Object.entries(brandingConfig.layout.margins).forEach(([key, margin]) => {
      if (typeof margin !== 'number' || margin < 10 || margin > 50) {
        errors.push(`Invalid margin for ${key}: must be between 10-50mm`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Color utility functions
export const colorUtils = {
  /**
   * Convert hex color to RGB array
   * @param {string} hex - Hex color code
   * @returns {number[]} - [r, g, b] array
   */
  hexToRgb: (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  },

  /**
   * Lighten a color by a percentage
   * @param {string} hex - Hex color code
   * @param {number} percent - Percentage to lighten (0-100)
   * @returns {string} - Lightened hex color
   */
  lighten: (hex, percent) => {
    const [r, g, b] = colorUtils.hexToRgb(hex);
    const factor = (100 - percent) / 100;
    return `#${[r, g, b].map(c => 
      Math.round(255 - (255 - c) * factor).toString(16).padStart(2, '0')
    ).join('')}`;
  },

  /**
   * Darken a color by a percentage
   * @param {string} hex - Hex color code
   * @param {number} percent - Percentage to darken (0-100)
   * @returns {string} - Darkened hex color
   */
  darken: (hex, percent) => {
    const [r, g, b] = colorUtils.hexToRgb(hex);
    const factor = (100 - percent) / 100;
    return `#${[r, g, b].map(c => 
      Math.round(c * factor).toString(16).padStart(2, '0')
    ).join('')}`;
  }
};

export default {
  DEFAULT_BRANDING,
  BRANDING_TEMPLATES,
  mergeBrandingConfig,
  validateBrandingConfig,
  colorUtils
};